import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TranscriptionState } from '@/hooks/useTranscription';
import { getPreferredMicDeviceId } from '@/lib/micDevice';
import type { Turn } from '@/lib/types';

// Deepgram streaming transcription (Sprint P5 S1; speaker turns added P6 S1).
// Mirrors the useTranscription interface so the two are interchangeable, adds
// running cost, and (P6) produces diarized speaker turns. Audio is captured with
// MediaRecorder and streamed to our api websocket proxy (which holds the key and
// runs Deepgram with diarize=true); the proxy streams back `turn` and `interim`
// messages. On any failure it calls onFallback so the provider can switch to Web
// Speech without the operator losing the hearing.

export interface DeepgramTranscriptionState extends TranscriptionState {
  /** Estimated Deepgram cost so far this session, in USD. */
  costUsd: number;
  /** Seconds of audio Deepgram has billed for. */
  audioSeconds: number;
  /** Diarized speaker turns (P6 S1). */
  turns: Turn[];
  /** True while the websocket dropped and we are re-establishing it
   *  (courtroom-ready sprint): listening visually continues, a quiet
   *  "reconnecting" state shows, and Web Speech fallback only fires once
   *  every reconnect attempt is exhausted. */
  reconnecting: boolean;
  /** Mic-health sprint: live input level (0..1) from a WebAudio analyser on the
   *  captured stream, so the operator can see the app hearing them. */
  inputLevel: number;
  /** Mic-health sprint: true once the captured stream has been effectively
   *  silent (RMS under threshold) for 10 consecutive seconds while listening.
   *  Cleared the moment real audio appears. */
  micSilent: boolean;
  /** Mic-health sprint: set when the selected input device could not be used
   *  (unplugged / permission) and capture fell back to the system default. */
  deviceNotice: string | null;
}

// Reconnect ladder: a network blip in court must not silently kill the
// transcript. Three attempts, short backoff; only then fall back to Web Speech.
const RECONNECT_DELAYS_MS = [1000, 2000, 4000];

// App-level keepalive: a small text frame every 20s so nginx (90s proxy
// timeouts) never sees a silent client even if the recorder stalls.
const KEEPALIVE_MS = 20_000;

// Watchdog: if the recorder produces no audio for this long while we believe
// we're listening, the capture pipeline has silently died (device switch,
// bluetooth drop) — recycle the connection instead of listening to nothing.
const NO_AUDIO_WATCHDOG_MS = 15_000;

// Mic-health sprint: today's live failure was a mic capturing pure silence —
// frames flowed, Deepgram billed, textLen stayed 0, and nothing on screen said
// why. The analyser below samples RMS off the captured stream; below this
// threshold for SILENT_MIC_AFTER_MS the UI shows a prominent warning.
const SILENCE_RMS_THRESHOLD = 0.01;
const SILENT_MIC_AFTER_MS = 10_000;
const METER_INTERVAL_MS = 200;

interface UseDeepgramOptions {
  initial?: string;
  initialTurns?: Turn[];
  matterId?: string | null;
  onFallback?: (carryTranscript: string) => void;
}

interface ServerMessage {
  type: string;
  text?: string;
  speaker?: number;
  audioSeconds?: number;
  costUsd?: number;
}

function deepgramSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof WebSocket !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  );
}

function pickMimeType(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return '';
}

function buildWsUrl(matterId: string | null | undefined): string {
  const rawBase = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');
  let origin: string;
  let basePath: string;
  if (/^https?:\/\//i.test(rawBase)) {
    const u = new URL(rawBase);
    origin = `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}`;
    basePath = u.pathname.replace(/\/$/, '');
  } else {
    origin = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;
    basePath = rawBase;
  }
  const q = matterId ? `?matterId=${encodeURIComponent(matterId)}` : '';
  return `${origin}${basePath}/transcribe/stream${q}`;
}

/** Merge a finalized turn into the list: consecutive turns by the same speaker
 *  join into one (mirrors the backend grouping) for a clean reading flow. */
export function mergeTurn(turns: Turn[], next: Turn): Turn[] {
  const text = next.text.trim();
  if (!text) {
    return turns;
  }
  const last = turns[turns.length - 1];
  if (last && last.speaker === next.speaker) {
    const merged = [...turns];
    merged[merged.length - 1] = { speaker: last.speaker, text: `${last.text} ${text}`.trim() };
    return merged;
  }
  return [...turns, { speaker: next.speaker, text }];
}

function turnsToText(turns: Turn[]): string {
  return turns.map((t) => t.text).join('\n');
}

export function useDeepgramTranscription(options: UseDeepgramOptions = {}): DeepgramTranscriptionState {
  const { matterId = null, initialTurns } = options;

  const [turns, setTurns] = useState<Turn[]>(initialTurns ?? []);
  const [interim, setInterim] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userStopped, setUserStopped] = useState(false);
  const [costUsd, setCostUsd] = useState(0);
  const [audioSeconds, setAudioSeconds] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [micSilent, setMicSilent] = useState(false);
  const [deviceNotice, setDeviceNotice] = useState<string | null>(null);

  const supported = deepgramSupported();
  const transcript = useMemo(() => turnsToText(turns), [turns]);

  const wsRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isListeningRef = useRef(false);
  const transcriptRef = useRef(transcript);
  const onFallbackRef = useRef(options.onFallback);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const keepaliveTimerRef = useRef<number | null>(null);
  const watchdogTimerRef = useRef<number | null>(null);
  const lastAudioAtRef = useRef(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const meterTimerRef = useRef<number | null>(null);
  const lastLoudAtRef = useRef(0);
  transcriptRef.current = transcript;
  onFallbackRef.current = options.onFallback;

  const stopRecorder = useCallback((): void => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    recorderRef.current = null;
  }, []);

  const closeSocket = useCallback((): void => {
    if (wsRef.current) {
      // Detach handlers first so this deliberate close doesn't look like a drop.
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
  }, []);

  const stopMeter = useCallback((): void => {
    if (meterTimerRef.current !== null) {
      window.clearInterval(meterTimerRef.current);
      meterTimerRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // ignore — context may already be closed
      });
      audioContextRef.current = null;
    }
    setInputLevel(0);
    setMicSilent(false);
  }, []);

  const teardown = useCallback((): void => {
    isListeningRef.current = false;
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (keepaliveTimerRef.current !== null) {
      window.clearInterval(keepaliveTimerRef.current);
      keepaliveTimerRef.current = null;
    }
    if (watchdogTimerRef.current !== null) {
      window.clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
    setReconnecting(false);
    stopMeter();
    stopRecorder();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    closeSocket();
  }, [stopMeter, stopRecorder, closeSocket]);

  const triggerFallback = useCallback(
    (message: string): void => {
      const carry = transcriptRef.current;
      teardown();
      setIsListening(false);
      setInterim('');
      setError(message);
      onFallbackRef.current?.(carry);
    },
    [teardown],
  );

  /** Live input level + silent-mic detection off the captured stream. Meter
   *  failure never blocks capture — worst case the level bar just stays flat. */
  function startMeter(stream: MediaStream): void {
    stopMeter();
    try {
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      lastLoudAtRef.current = Date.now();
      meterTimerRef.current = window.setInterval(() => {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (let i = 0; i < samples.length; i++) {
          sum += samples[i]! * samples[i]!;
        }
        const rms = Math.sqrt(sum / samples.length);
        // Quantize so a steady level doesn't re-render 5×/second.
        setInputLevel(Math.round(Math.min(1, rms * 6) * 20) / 20);
        if (rms >= SILENCE_RMS_THRESHOLD) {
          lastLoudAtRef.current = Date.now();
          setMicSilent(false);
        } else if (Date.now() - lastLoudAtRef.current >= SILENT_MIC_AFTER_MS) {
          setMicSilent(true);
        }
      }, METER_INTERVAL_MS);
    } catch {
      // WebAudio unavailable; transcription still works without the meter.
    }
  }

  /** Open the mic honouring the operator's saved device choice; if that device
   *  is gone (unplugged, permissions changed) fall back to the system default
   *  and say so via deviceNotice rather than failing silently. */
  async function acquireStream(): Promise<MediaStream> {
    const preferred = getPreferredMicDeviceId();
    if (preferred) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: preferred } },
        });
        setDeviceNotice(null);
        return stream;
      } catch {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        setDeviceNotice('Selected microphone unavailable — using the system default instead.');
        return stream;
      }
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setDeviceNotice(null);
    return stream;
  }

  function startRecorder(stream: MediaStream, ws: WebSocket): void {
    if (!isListeningRef.current) {
      return;
    }
    stopRecorder(); // never leave two recorders feeding one socket
    const mimeType = pickMimeType();
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    recorderRef.current = recorder;
    lastAudioAtRef.current = Date.now();
    recorder.ondataavailable = (e: BlobEvent): void => {
      if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
        lastAudioAtRef.current = Date.now();
        ws.send(e.data);
      }
    };
    // A capture failure (device switch, bluetooth drop) must not be silent:
    // recycle the socket + recorder through the normal reconnect ladder.
    recorder.onerror = (): void => {
      if (isListeningRef.current) {
        scheduleReconnect();
      }
    };
    recorder.start(250); // 250ms audio frames
  }

  /** Keepalive + stalled-audio watchdog, one pair per live socket. */
  function startTimers(ws: WebSocket): void {
    if (keepaliveTimerRef.current !== null) {
      window.clearInterval(keepaliveTimerRef.current);
    }
    keepaliveTimerRef.current = window.setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // ignore
        }
      }
    }, KEEPALIVE_MS);
    if (watchdogTimerRef.current !== null) {
      window.clearInterval(watchdogTimerRef.current);
    }
    watchdogTimerRef.current = window.setInterval(() => {
      if (!isListeningRef.current) {
        return;
      }
      const recorder = recorderRef.current;
      const stalled = Date.now() - lastAudioAtRef.current > NO_AUDIO_WATCHDOG_MS;
      if (stalled && recorder && recorder.state === 'recording') {
        scheduleReconnect();
      }
    }, NO_AUDIO_WATCHDOG_MS / 3);
  }

  /** Open (or re-open) the proxy websocket over an existing mic stream and wire
   *  the message handlers. Reused by both the initial start and reconnects. */
  function openSocket(stream: MediaStream): void {
    const ws = new WebSocket(buildWsUrl(matterId));
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';

    ws.onmessage = (ev: MessageEvent): void => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === 'ready') {
        reconnectAttemptRef.current = 0;
        setReconnecting(false);
        setError(null);
        startRecorder(stream, ws);
        startTimers(ws);
      } else if (msg.type === 'turn' && typeof msg.text === 'string') {
        const speaker = typeof msg.speaker === 'number' ? msg.speaker : 0;
        setTurns((prev) => mergeTurn(prev, { speaker, text: msg.text ?? '' }));
        setInterim('');
      } else if (msg.type === 'interim' && typeof msg.text === 'string') {
        setInterim(msg.text.trim());
      } else if (msg.type === 'cost') {
        if (typeof msg.costUsd === 'number') {
          setCostUsd(msg.costUsd);
        }
        if (typeof msg.audioSeconds === 'number') {
          setAudioSeconds(msg.audioSeconds);
        }
      } else if (msg.type === 'error' || msg.type === 'upstream_closed') {
        scheduleReconnect();
      }
    };

    ws.onerror = (): void => {
      if (isListeningRef.current) {
        scheduleReconnect();
      }
    };

    ws.onclose = (): void => {
      if (isListeningRef.current) {
        scheduleReconnect();
      }
    };
  }

  /** A drop while listening: retry on the backoff ladder before giving up to
   *  Web Speech. The mic stream is kept alive so reconnects are silent. */
  function scheduleReconnect(): void {
    if (!isListeningRef.current || reconnectTimerRef.current !== null) {
      return;
    }
    stopRecorder();
    closeSocket();
    const attempt = reconnectAttemptRef.current;
    const delay = RECONNECT_DELAYS_MS[attempt];
    if (delay === undefined) {
      triggerFallback('Transcription connection lost; switched to Web Speech.');
      return;
    }
    reconnectAttemptRef.current = attempt + 1;
    setReconnecting(true);
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      if (!isListeningRef.current) {
        return;
      }
      const stream = streamRef.current;
      if (stream && stream.getTracks().some((t) => t.readyState === 'live')) {
        openSocket(stream);
      } else {
        // Mic stream died too (device unplugged / permission revoked). Try to
        // re-acquire — saved device first, then system default — before giving
        // up to Web Speech.
        stopMeter();
        acquireStream()
          .then((fresh) => {
            if (!isListeningRef.current) {
              fresh.getTracks().forEach((t) => t.stop());
              return;
            }
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = fresh;
            startMeter(fresh);
            openSocket(fresh);
          })
          .catch(() => {
            triggerFallback('Microphone lost; switched to Web Speech.');
          });
      }
    }, delay);
  }

  const start = useCallback((): void => {
    if (!supported || isListeningRef.current) {
      return;
    }
    isListeningRef.current = true;
    setIsListening(true);
    setUserStopped(false);
    setError(null);
    reconnectAttemptRef.current = 0;

    acquireStream()
      .then((stream) => {
        if (!isListeningRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        startMeter(stream);
        openSocket(stream);
      })
      .catch(() => {
        triggerFallback('Microphone unavailable for Deepgram; switched to Web Speech.');
      });
    // openSocket/scheduleReconnect are stable function declarations within this
    // hook instance; matterId is the only real dependency they close over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, matterId, triggerFallback]);

  const stop = useCallback((): void => {
    if (!isListeningRef.current && !wsRef.current) {
      return;
    }
    setUserStopped(true);
    isListeningRef.current = false;
    setIsListening(false);
    setInterim('');
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'stop' }));
      } catch {
        // ignore
      }
    }
    teardown();
  }, [teardown]);

  const reset = useCallback((): void => {
    setTurns([]);
    setInterim('');
  }, []);

  useEffect(() => teardown, [teardown]);

  return {
    transcript,
    interim,
    isListening,
    supported,
    error,
    userStopped,
    costUsd,
    audioSeconds,
    turns,
    reconnecting,
    inputLevel,
    micSilent,
    deviceNotice,
    start,
    stop,
    reset,
  };
}
